import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LockKey, CheckCircle, WarningCircle, Trash } from "@phosphor-icons/react";

const RULES = [
  { id: "len",    label: "At least 14 characters",     test: (v) => v.length >= 14 },
  { id: "letter", label: "Contains a letter",           test: (v) => /[A-Za-z]/.test(v) },
  { id: "digit",  label: "Contains a digit",            test: (v) => /\d/.test(v) },
  { id: "sym",    label: "Contains a symbol",           test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function PasswordCard() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasPassword, setHasPassword] = useState(!!user?.has_password);

  // Only admin accounts can have password auth.
  if (user?.role !== "admin") return null;

  const allValid = RULES.every((r) => r.test(newPassword));
  const matches = confirmPassword === newPassword && confirmPassword.length > 0;
  const canSubmit = !saving && allValid && matches && (!hasPassword || currentPassword.length > 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!allValid) {
      setError("Password doesn\u2019t meet the strength policy.");
      return;
    }
    if (!matches) {
      setError("New password and confirmation don\u2019t match.");
      return;
    }
    setSaving(true);
    try {
      const body = { new_password: newPassword };
      if (hasPassword) body.current_password = currentPassword;
      await api.post("/auth/password/set", body);
      setSuccess("Password updated. Use it on the sign-in page next time.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setHasPassword(true);
    } catch (e2) {
      setError(e2.response?.data?.detail || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm(
      "Remove password login? Sign-in will fall back to magic link only. " +
      "You can add it back anytime from this page.",
    )) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await api.delete("/auth/password");
      setHasPassword(false);
      setSuccess("Password removed. Magic-link sign-in only.");
    } catch (e2) {
      setError(e2.response?.data?.detail || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="sw-card p-6" data-testid="password-card">
      <header className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-md border border-brand-cyan/40 bg-brand-cyan/10 flex items-center justify-center">
          <LockKey size={18} weight="bold" className="text-brand-cyan" />
        </div>
        <div>
          <h2 className="font-display text-base font-bold tracking-tight" data-testid="password-card-title">
            Password sign-in
          </h2>
          <p className="text-[11px] text-slate-400">
            Admin accounts can sign in with a password instead of a magic link. 2FA still applies.
          </p>
        </div>
      </header>

      {hasPassword && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[10px] font-semibold uppercase tracking-[0.15em]"
          data-testid="password-status-set">
          <CheckCircle size={11} weight="bold" /> Password set
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-4 space-y-3" data-testid="password-form">
        {hasPassword && (
          <div>
            <label htmlFor="pwd-current" className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">
              Current password
            </label>
            <input
              id="pwd-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="pwd-current-input"
              className="w-full"
            />
          </div>
        )}

        <div>
          <label htmlFor="pwd-new" className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">
            {hasPassword ? "New password" : "Password"}
          </label>
          <input
            id="pwd-new"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="pwd-new-input"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="pwd-confirm" className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">
            Confirm password
          </label>
          <input
            id="pwd-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="pwd-confirm-input"
            className="w-full"
          />
        </div>

        <ul className="space-y-1 pt-1" data-testid="pwd-rules">
          {RULES.map((r) => {
            const ok = r.test(newPassword);
            return (
              <li key={r.id} data-testid={`pwd-rule-${r.id}`}
                className={`flex items-center gap-2 text-[11px] ${ok ? "text-emerald-400" : "text-slate-500"}`}>
                {ok
                  ? <CheckCircle size={12} weight="bold" />
                  : <WarningCircle size={12} weight="bold" />}
                {r.label}
              </li>
            );
          })}
          <li className={`flex items-center gap-2 text-[11px] ${matches ? "text-emerald-400" : "text-slate-500"}`}
            data-testid="pwd-rule-match">
            {matches
              ? <CheckCircle size={12} weight="bold" />
              : <WarningCircle size={12} weight="bold" />}
            Both entries match
          </li>
        </ul>

        {error && (
          <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded p-2"
            data-testid="pwd-error">
            {error}
          </div>
        )}
        {success && (
          <div className="text-xs text-emerald-400 border border-emerald-400/30 bg-emerald-400/5 rounded p-2"
            data-testid="pwd-success">
            {success}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          {hasPassword ? (
            <button type="button" onClick={onRemove}
              disabled={saving}
              data-testid="pwd-remove-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] border border-red-400/30 hover:bg-red-400/10 text-red-400 rounded transition-colors disabled:opacity-40">
              <Trash size={12} weight="bold" />
              Remove password
            </button>
          ) : <span />}

          <button type="submit"
            disabled={!canSubmit}
            data-testid="pwd-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] border border-brand-cyan/50 bg-brand-cyan/15 text-brand-cyan hover:bg-brand-cyan/25 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <LockKey size={12} weight="bold" />
            {saving ? "Saving…" : hasPassword ? "Change password" : "Set password"}
          </button>
        </div>
      </form>
    </section>
  );
}
